import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Boxes,
  ClipboardCheck,
  MessageSquarePlus,
  Trash2,
  Megaphone,
  Send,
  UserCog,
  Building2,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStudioList } from "@/lib/useStudioList";
import { Kpi, PageHead } from "@/components/Bits";
import { HqBreakdownDialog } from "@/components/HqBreakdownDialog";
import { WhatsAppPendingCard } from "@/components/WhatsAppPendingCard";
import { ProtectionTasks } from "@/components/ProtectionTasks";
import StudioPerformance from "@/pages/StudioPerformance";
import { BrandBriefPanel, BcMonthlyMessage } from "@/components/BrandConsultant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const month = new Date().toISOString().slice(0, 7);
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

const CEO_ADMIN_LINKS = [
  { to: "/users", label: "Users", desc: "People & roles", Icon: UserCog },
  { to: "/studios", label: "Studios", desc: "Studios, staff & onboarding", Icon: Building2 },
  { to: "/configuration", label: "Configuration", desc: "Roles, catalog & settings", Icon: Settings },
  { to: "/warranties", label: "Warranties", desc: "Certificates & records", Icon: ShieldCheck },
];

const RatioTable = ({ rows, label, testid }) => (
  <Card className="border-brand-line shadow-none">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">{label}</CardTitle>
    </CardHeader>
    <CardContent className="overflow-x-auto p-0">
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Leads</TableHead>
            <TableHead className="text-right">Delivered</TableHead>
            <TableHead className="text-right">Ratio</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-6 text-center text-sm text-slate-400">
                No data yet.
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.studio_id || r.cs_person_id} className="hover:bg-brand-tint/60">
              <TableCell className="font-medium text-brand-ink">{r.name}</TableCell>
              <TableCell className="text-right tabular-nums">{r.leads}</TableCell>
              <TableCell className="text-right tabular-nums">{r.delivered}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums text-brand">
                {r.ratio_pct}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export function CEODashboard({ adminNav }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const { rows: studioList } = useStudioList(true);
  const [ld, setLd] = useState({ studios: [], reps: [] });
  const [crm, setCrm] = useState(null);
  const [stock, setStock] = useState([]);
  const [mm, setMm] = useState({ items: [] });
  const [logs, setLogs] = useState([]);
  const [hq, setHq] = useState(null);
  const [salesT, setSalesT] = useState([]);
  const [bmsg, setBmsg] = useState({ audience: "all", text: "" });
  const [ceoMsgs, setCeoMsgs] = useState([]);
  const [form, setForm] = useState({ target_type: "studio", target_label: "", kind: "Call", note: "" });
  const [drill, setDrill] = useState(null);
  const [pd, setPd] = useState(null);
  const [pdScope, setPdScope] = useState("all");
  const [prodGoal, setProdGoal] = useState("");

  const loadPd = useCallback(() => {
    api.get("/reports/products-delivered", { params: { month } })
      .then(({ data }) => setPd(data)).catch(() => {});
  }, []);
  const loadLogs = useCallback(() => {
    api.get("/ceo/interactions").then(({ data }) => setLogs(data)).catch(() => {});
  }, []);
  const loadMsgs = useCallback(() => {
    api.get("/ceo/messages").then(({ data }) => setCeoMsgs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = () => {};
    api.get("/reports/lead-delivered").then(({ data }) => setLd(data)).catch(q);
    api.get("/crm/summary").then(({ data }) => setCrm(data)).catch(q);
    api.get("/stock/overview").then(({ data }) => setStock(data.rows || [])).catch(q);
    api.get("/mm-targets", { params: { month } }).then(({ data }) => setMm(data)).catch(q);
    api.get("/reports/hq").then(({ data }) => setHq(data)).catch(q);
    api.get("/sales-targets", { params: { month } }).then(({ data }) => setSalesT(data)).catch(q);
    loadPd();
    loadLogs();
    loadMsgs();
  }, [loadPd, loadLogs, loadMsgs]);

  const totals = useMemo(
    () =>
      studioList.reduce(
        (a, s) => ({
          revenue: a.revenue + (s.month_revenue || 0),
          profit: a.profit + (s.month_profit || 0),
          jobs: a.jobs + (s.month_jobs || 0),
        }),
        { revenue: 0, profit: 0, jobs: 0 }
      ),
    [studioList]
  );

  const hqMonth = (hq?.monthly || []).find((m) => m.month === month) || { revenue: 0, profit: 0 };

  const lowStock = stock.filter((s) => s.hq_low || s.hq_out);
  const mmPending = (mm.items || []).filter((i) => !i.done);

  const isCeo = ["network_admin", "ceo"].includes(user.role);
  const prettyProduct = (n) =>
    String(n || "")
      .split(" ")
      .map((w) => (/^ppf$/i.test(w) ? "PPF" : /^vlt$/i.test(w) ? "VLT" : w.charAt(0) + w.slice(1).toLowerCase()))
      .join(" ");
  const pdCounts = !pd
    ? {}
    : pdScope === "all"
    ? pd.all
    : pdScope === "non_accredited"
    ? pd.non_accredited
    : (pd.studios.find((s) => s.studio_id === pdScope)?.counts || {});
  const pdTotal = Math.round(Object.values(pdCounts).reduce((a, b) => a + b, 0) * 100) / 100;
  const pdScopeLabel =
    pdScope === "all"
      ? "All recipients"
      : pdScope === "non_accredited"
      ? "Non-Accredited Detailers"
      : pd?.studios.find((s) => s.studio_id === pdScope)?.name || "Studio";

  const saveProdTarget = async () => {
    try {
      await api.put("/product-target", { month, goal: Number(prodGoal || 0) });
      toast.success("Products target saved");
      setProdGoal("");
      loadPd();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addLog = async () => {
    if (!form.note.trim()) return;
    try {
      await api.post("/ceo/interactions", form);
      setForm({ target_type: form.target_type, target_label: "", kind: form.kind, note: "" });
      loadLogs();
      toast.success("Interaction logged");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const delLog = async (id) => {
    await api.delete(`/ceo/interactions/${id}`).catch(() => {});
    loadLogs();
  };

  const sendMsg = async () => {
    if (!bmsg.text.trim()) return;
    try {
      const { data } = await api.post("/ceo/message", bmsg);
      toast.success(`Sent to ${data.recipients} recipient(s)`);
      setBmsg({ audience: bmsg.audience, text: "" });
      loadMsgs();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="ceo-dashboard">
      <PageHead
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle="Network command centre — revenue, studios, targets and team activity at a glance"
      />

      <WhatsAppPendingCard />

      {/* 1 · HQ revenue & profit — this month (compact, not full-width on mobile) */}
      <div className="mb-6 grid grid-cols-2 gap-4" data-testid="ceo-revenue">
        <Kpi label="HQ revenue (this month)" value={money(hqMonth.revenue || 0)} hint="material sales · this month" testid="ceo-kpi-revenue" onClick={() => setDrill("revenue")} />
        <Kpi label="HQ profit (this month)" value={money(hqMonth.profit || 0)} accent="text-emerald-600" hint="HQ margin · this month" testid="ceo-kpi-profit" onClick={() => setDrill("profit")} />
      </div>

      {/* 2 · Products delivered this month, by recipient */}
      <Card className="mb-6 border-brand-line shadow-none" data-testid="ceo-products-delivered">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
          <CardTitle className="text-base">Products delivered · {month}</CardTitle>
          <Select value={pdScope} onValueChange={setPdScope}>
            <SelectTrigger className="min-h-[40px] w-full sm:w-64" data-testid="pd-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All recipients</SelectItem>
              <SelectItem value="non_accredited">Non-Accredited Detailers</SelectItem>
              {(pd?.studios || []).map((s) => (
                <SelectItem key={s.studio_id} value={s.studio_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-slate-500">
            {pdScopeLabel} · <span className="font-semibold text-brand-ink" data-testid="pd-total">{pdTotal}</span> unit(s) delivered
          </p>
          {(!pd || pd.products.length === 0) ? (
            <p className="text-sm text-slate-500" data-testid="pd-empty">No products delivered this month yet.</p>
          ) : (
            <div className="space-y-1.5" data-testid="pd-breakdown">
              {pd.products.map((name) => (
                <div key={name} className="flex items-center justify-between rounded-md border border-brand-line px-3 py-2 text-sm">
                  <span className="font-medium text-brand-ink">{prettyProduct(name)}</span>
                  <span className="tabular-nums text-slate-600">{pdCounts[name] || 0}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3 · Total HQ installer jobs done this month */}
      <div className="mb-6 max-w-sm" data-testid="ceo-hq-jobs">
        <Kpi label="Total HQ installer jobs done (this month)" value={pd?.hq_installer_jobs ?? 0} hint="delivered by HQ crews · this month" testid="ceo-kpi-hq-jobs" />
      </div>

      {/* 4 · Lead → delivered — two separate rows */}
      <div className="mb-6 space-y-4">
        <RatioTable rows={ld.studios} label="Lead → delivered · by studio" testid="ceo-ld-studios" />
        <RatioTable rows={ld.reps} label="Lead → delivered · by Central Sales team" testid="ceo-ld-reps" />
      </div>

      {/* 5 · Targets */}
      <Card className="mb-6 border-brand-line shadow-none" data-testid="ceo-targets">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Targets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Salesperson job targets · {month}
            </p>
            {salesT.length === 0 ? (
              <p className="text-sm text-slate-500">No salesperson targets set yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="ceo-sales-target-rows">
                {salesT.map((s) => (
                  <div
                    key={s.user_id}
                    className="flex items-center justify-between rounded-md border border-brand-line px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-brand-ink">{s.name}</span>
                    <span className="tabular-nums text-slate-600">
                      {s.achieved}/{s.goal || 0} delivered ·{" "}
                      <span className={s.pct >= 100 ? "text-emerald-600" : "text-amber-600"}>{s.pct}%</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-brand-line pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Marketing Manager target · {month}
            </p>
            <p className="mt-1 text-sm text-brand-ink" data-testid="ceo-mm-progress">
              {(mm.items || []).filter((i) => i.done).length}/{(mm.items || []).length} done
            </p>
          </div>
          <div className="border-t border-brand-line pt-3" data-testid="ceo-product-target">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sales Manager (Products) target · {month}
            </p>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-brand-ink" data-testid="ceo-product-target-progress">
                {pd?.product_target?.achieved ?? 0}/{pd?.product_target?.goal ?? 0} units ·{" "}
                <span className={(pd?.product_target?.pct ?? 0) >= 100 ? "text-emerald-600" : "text-amber-600"}>
                  {pd?.product_target?.pct ?? 0}%
                </span>
              </p>
              {isCeo && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    className="min-h-[40px] w-28"
                    placeholder="Goal (units)"
                    data-testid="ceo-product-goal-input"
                    value={prodGoal}
                    onChange={(e) => setProdGoal(e.target.value)}
                  />
                  <Button size="sm" className="min-h-[40px]" data-testid="ceo-product-goal-save" onClick={saveProdTarget} disabled={prodGoal === ""}>
                    Set goal
                  </Button>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => nav("/monthly-targets")}
            data-testid="ceo-targets-manage"
            className="text-sm font-medium text-brand hover:underline"
          >
            Set / edit targets →
          </button>
        </CardContent>
      </Card>

      {/* 6 · Google reviews this month */}
      <div className="mb-6 max-w-sm" data-testid="ceo-google-reviews">
        <Kpi
          label="Google reviews (this month)"
          value={crm?.google_reviews?.confirmed ?? 0}
          accent="text-emerald-600"
          hint={`${crm?.google_reviews?.sent ?? 0} requested this month`}
          testid="ceo-kpi-reviews-month"
        />
      </div>

      {/* ───────── Everything else ───────── */}
      {/* All-time HQ revenue + accredited/non-accredited split */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ceo-alltime">
        <Kpi label="HQ revenue (all-time)" value={money(hq?.totals?.revenue || 0)} hint={`material ${money(hq?.totals?.material_revenue || 0)} + installation ${money(hq?.totals?.installation_revenue || 0)} + sales-acq ${money(hq?.totals?.sales_commission_revenue || 0)}`} testid="ceo-kpi-revenue-all" onClick={() => setDrill("revenue")} />
        <Kpi label="HQ profit (all-time)" value={money(hq?.totals?.profit || 0)} accent="text-emerald-600" hint="HQ margin · all orders except returns" testid="ceo-kpi-profit-all" onClick={() => setDrill("profit")} />
        <Kpi label="Jobs delivered (this month)" value={totals.jobs} hint="across all studios" testid="ceo-kpi-jobs" />
        <Kpi label="Active studios" value={studioList.length} testid="ceo-kpi-studios" />
      </div>
      <div className="mb-6 grid gap-4 sm:grid-cols-2" data-testid="ceo-material-split">
        <Kpi
          label="Accredited studio purchases"
          value={money(hq?.totals?.accredited_revenue || 0)}
          hint={`profit ${money(hq?.totals?.accredited_profit || 0)} · material sales to network studios`}
          testid="ceo-kpi-accredited"
          onClick={() => setDrill("revenue")}
        />
        <Kpi
          label="Non-accredited detailer purchases"
          value={money(hq?.totals?.detailer_revenue || 0)}
          hint={`profit ${money(hq?.totals?.detailer_profit || 0)} · B2B sales to outside detailers`}
          testid="ceo-kpi-detailer"
          onClick={() => setDrill("revenue")}
        />
      </div>
      <HqBreakdownDialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)} focus={drill || "revenue"} />

      {/* Pending verifications / approvals */}
      <Card className="mb-6 border-amber-200 bg-amber-50/60 shadow-none" data-testid="ceo-pending">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-amber-900">
            <ClipboardCheck className="h-4 w-4" /> Waiting on you
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 text-sm text-amber-900">
          <button
            onClick={() => nav("/monthly-targets")}
            className="rounded-md border border-amber-300 bg-white px-3 py-1.5 font-medium hover:bg-amber-100"
            data-testid="ceo-pending-mm"
          >
            {mmPending.length} Marketing-Manager target(s) not yet done
          </button>
          <button
            onClick={() => document.getElementById("ceo-bc-brief")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-md border border-amber-300 bg-white px-3 py-1.5 font-medium hover:bg-amber-100"
            data-testid="ceo-pending-bc"
          >
            Review Brand Consultant brief
          </button>
        </CardContent>
      </Card>

      {/* CRM performance + Google reviews */}
      {crm && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ceo-crm">
          <Kpi
            label="Warranties registered"
            value={crm.warranties.registered}
            hint={`${crm.warranties.timely} within 7 days`}
            testid="ceo-kpi-warranty"
          />
          <Kpi
            label="Protection plans"
            value={crm.protection.registered}
            hint={`${crm.protection.pending} pending`}
            testid="ceo-kpi-protection"
          />
          <Kpi
            label="Google reviews (mo)"
            value={crm.google_reviews.confirmed}
            hint={`${crm.google_reviews.sent} requested`}
            testid="ceo-kpi-reviews"
          />
          <Kpi
            label="Feedback collected"
            value={`${crm.feedback.done}/${crm.feedback.delivered_jobs}`}
            hint="delivered jobs"
            testid="ceo-kpi-feedback"
          />
        </div>
      )}

      {/* Stock position */}
      <Card className="mb-6 border-brand-line shadow-none" data-testid="ceo-stock">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Boxes className="h-4 w-4 text-slate-400" /> Stock position — HQ dispatch vs studio
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lowStock.length === 0 ? (
            <p className="text-sm text-slate-500">All HQ materials are above their low-stock threshold.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="ceo-low-stock">
              {lowStock.map((s) => (
                <span
                  key={s.sku || s.name}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                    s.hq_out ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {s.name || s.sku}: {s.hq_out ? "OUT" : `${s.hq_quantity} left`}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Brand Consultant monthly message (to studios) + brief & activity */}
      <BcMonthlyMessage />
      <div className="mb-6" id="ceo-bc-brief">
        <BrandBriefPanel />
      </div>

      {/* Admin console quick links (network admin only) */}
      {adminNav && (
        <div className="mb-6" data-testid="ceo-admin-links">
          <p className="mb-3 font-display text-lg font-semibold text-brand-ink">Admin console</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CEO_ADMIN_LINKS.map(({ to, label, desc, Icon }) => (
              <button
                key={to}
                data-testid={`admin-link-${label.toLowerCase()}`}
                onClick={() => adminNav(to)}
                className="flex flex-col items-start gap-2 rounded-lg border border-brand-line bg-white p-5 text-left transition-shadow duration-200 hover:shadow-sm"
              >
                <Icon className="h-6 w-6 text-crimson-500" strokeWidth={1.8} />
                <p className="font-display text-base font-semibold text-brand-ink">{label}</p>
                <p className="text-xs text-slate-500">{desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Studio-wise performance */}
      <StudioPerformance rows={studioList} onOpen={(id) => nav(`/studios/${id}`)} />

      {/* CEO's own interactions */}
      <Card className="mt-6 border-brand-line shadow-none" data-testid="ceo-interactions">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquarePlus className="h-4 w-4 text-slate-400" /> My interactions with team & studios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-[9rem_1fr_8rem_auto]">
            <Select value={form.target_type} onValueChange={(v) => setForm((f) => ({ ...f, target_type: v }))}>
              <SelectTrigger className="min-h-[44px]" data-testid="ceo-int-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="studio">Studio</SelectItem>
                <SelectItem value="team">Central team</SelectItem>
                <SelectItem value="person">Person</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="min-h-[44px]"
              placeholder="Who? (studio / person / team name)"
              data-testid="ceo-int-label"
              value={form.target_label}
              onChange={(e) => setForm((f) => ({ ...f, target_label: e.target.value }))}
            />
            <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger className="min-h-[44px]" data-testid="ceo-int-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Call", "Meeting", "WhatsApp", "Email", "Note"].map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button className="min-h-[44px]" data-testid="ceo-int-save" onClick={addLog} disabled={!form.note.trim()}>
              Log
            </Button>
          </div>
          <Textarea
            rows={2}
            placeholder="What was discussed / decided?"
            data-testid="ceo-int-note"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
          <div className="space-y-1.5">
            {logs.length === 0 && <p className="text-sm text-slate-500">No interactions logged yet.</p>}
            {logs.map((l) => (
              <div
                key={l.id}
                data-testid={`ceo-int-row-${l.id}`}
                className="flex items-start justify-between gap-3 rounded-md border border-brand-line p-3 text-sm"
              >
                <div>
                  <p className="text-brand-ink">{l.note}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {l.kind} · {l.target_type}
                    {l.target_label ? ` · ${l.target_label}` : ""} · {(l.created_at || "").slice(0, 10)}
                  </p>
                </div>
                <button
                  onClick={() => delLog(l.id)}
                  data-testid={`ceo-int-del-${l.id}`}
                  className="text-slate-400 hover:text-rose-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CEO broadcast to studios & central team */}
      <Card className="mt-6 border-brand-line shadow-none" data-testid="ceo-broadcast">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="h-4 w-4 text-slate-400" /> Message studios & central team
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={bmsg.audience} onValueChange={(v) => setBmsg((m) => ({ ...m, audience: v }))}>
              <SelectTrigger className="min-h-[44px] w-48" data-testid="ceo-msg-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="studios">All studios</SelectItem>
                <SelectItem value="central">Central team</SelectItem>
                <SelectItem value="all">Everyone</SelectItem>
              </SelectContent>
            </Select>
            <Button className="min-h-[44px]" data-testid="ceo-msg-send" onClick={sendMsg} disabled={!bmsg.text.trim()}>
              <Send className="mr-2 h-4 w-4" /> Send
            </Button>
          </div>
          <Textarea
            rows={2}
            placeholder="Share an update or suggestion with your team…"
            data-testid="ceo-msg-text"
            value={bmsg.text}
            onChange={(e) => setBmsg((m) => ({ ...m, text: e.target.value }))}
          />
          <div className="space-y-2" data-testid="ceo-msg-history">
            {ceoMsgs.length === 0 && (
              <p className="text-sm text-slate-500">No messages sent yet.</p>
            )}
            {ceoMsgs.slice(0, 6).map((m) => (
              <div key={m.id} data-testid={`ceo-msg-${m.id}`} className="rounded-md border border-brand-line p-3 text-sm">
                <p className="text-brand-ink">{m.text}</p>
                <p className="mt-0.5 text-xs text-slate-500" data-testid={`ceo-msg-reads-${m.id}`}>
                  to {m.audience} · {(m.created_at || "").slice(0, 10)} ·{" "}
                  {m.audience === "central"
                    ? `${m.acked_count} read`
                    : `${m.studios_acked}/${m.studios_total} studios read`}
                </p>
                {m.replies?.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-brand-line pt-2" data-testid={`ceo-msg-replies-${m.id}`}>
                    {m.replies.map((r, i) => (
                      <p key={i} className="text-xs text-slate-600">
                        <span className="font-medium text-brand-ink">{r.studio_name || r.user_name}:</span>{" "}
                        {r.reply}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="mt-2"><ProtectionTasks /></div>
    </div>
  );
}

export function CeoMessage() {
  const [m, setM] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/ceo/message/current").then(({ data }) => setM(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  if (!m || !m.has || !m.text) return null;
  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/ceo/message/${m.id}/ack`, { reply });
      toast.success(reply.trim() ? "Reply sent" : "Acknowledged");
      setReply("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="mb-6 border-brand bg-brand-tint/30 shadow-none" data-testid="ceo-message-card">
      <CardHeader className="flex-row items-center gap-2 space-y-0 pb-2">
        <Megaphone className="h-4 w-4 text-brand" />
        <CardTitle className="text-base">Message from the CEO</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-wrap text-sm text-brand-ink">{m.text}</p>
        {m.by_name && <p className="mt-1 text-xs text-slate-500">— {m.by_name}</p>}
        <div className="mt-3 border-t border-brand-line pt-3">
          {m.acked ? (
            <p className="text-sm font-medium text-emerald-700" data-testid="ceo-message-acked">
              ✓ You acknowledged this{m.my_reply ? ` — "${m.my_reply}"` : ""}
            </p>
          ) : (
            <div className="space-y-2">
              <Textarea
                rows={2}
                placeholder="Add a reply (optional)…"
                data-testid="ceo-message-reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <Button size="sm" data-testid="ceo-message-ack" onClick={ack} disabled={busy}>
                {reply.trim() ? "Send reply & acknowledge" : "Acknowledge"}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

